import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, addDaysISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, SelectField } from "@/components/crm/fields";

const TYPES = [["follow_up", "Follow-up"], ["call", "Call"], ["meeting", "Meeting"], ["email", "Email"], ["quotation", "Quotation"], ["other", "Other"]];

export default function TaskDialog({ open, onOpenChange, lead, onSaved }) {
  const { can } = useAuth();
  const { activeUsers } = useMeta();
  const [f, setF] = useState({});
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ title: lead ? `Follow up with ${lead.contact_person || lead.company}` : "", type: "follow_up", due_date: addDaysISO(1), lead_id: lead?.lead_id || "", assignee_id: lead?.owner_id || "" });
    if (!lead) api.get("/leads", { params: { limit: 300, sort: "company", order: "asc" } }).then((r) => setLeads(r.data.items));
  }, [open, lead]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  const save = async () => {
    if (!f.title?.trim() || !f.due_date) { toast.error("Title and due date are required"); return; }
    setSaving(true);
    try {
      const body = { ...f, lead_id: f.lead_id || null, assignee_id: f.assignee_id || null, due_time: f.due_time || null };
      await api.post("/tasks", body);
      toast.success("Follow-up scheduled");
      onOpenChange(false);
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Schedule follow-up</DialogTitle>
          <DialogDescription>{lead ? lead.company : "Create a task or reminder."}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Title" className="col-span-2"><Input data-testid="task-title-input" value={f.title || ""} onChange={set("title")} className="h-9" /></Field>
          <Field label="Type"><SelectField testid="task-type-select" value={f.type} onChange={set("type")} options={TYPES} allowNone={false} /></Field>
          <Field label="Due date"><Input data-testid="task-due-input" type="date" value={f.due_date || ""} onChange={set("due_date")} className="h-9" /></Field>
          <Field label="Time" hint="(optional)"><Input data-testid="task-time-input" type="time" value={f.due_time || ""} onChange={set("due_time")} className="h-9" /></Field>
          {can("lead:assign") && (
            <Field label="Assign to"><SelectField testid="task-assignee-select" value={f.assignee_id} onChange={set("assignee_id")} placeholder="Lead owner / me"
              options={activeUsers.map((u) => [u.user_id, u.name])} /></Field>
          )}
          {!lead && (
            <Field label="Related lead" className="col-span-2"><SelectField testid="task-lead-select" value={f.lead_id} onChange={set("lead_id")} placeholder="No lead"
              options={leads.map((l) => [l.lead_id, l.company])} /></Field>
          )}
          <Field label="Notes" className="col-span-2"><Textarea data-testid="task-notes-input" rows={2} value={f.notes || ""} onChange={set("notes")} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-[#0B3C2D] hover:bg-[#072B20]" disabled={saving} onClick={save} data-testid="task-save-button">{saving ? "Saving…" : "Schedule"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
